import {Node} from 'posthtml-parser';
import {Line, LineItemType} from '@gongxigames/framework/core/fragments/display/richText/Line';
import {RichTextTextStyle, VerticalAlign} from '@gongxigames/framework/core/fragments/display/richText/TextStyle';
import {Container, Point, Rectangle, Renderer, SCALE_MODES, Sprite} from 'pixi.js';
import {ResourceLoader} from '@gongxigames/framework/core/services/resourceLoader/ResourceLoader';

export type StackLevel = {
    style: RichTextTextStyle;
    /**
     * container for the current style
     */
    container: Container;
    /**
     * current level of nesting in node graph
     */
    level: number;
    /**
     * used for cursor positioning relative for parent styles
     */
    cursorOffset?: Point;
    isMask?: boolean;
};

/**
 * Saves the cursor current state, used for building up text from node graph
 */
export class CursorContext {
    protected readonly _stylesAvailable: Record<string, RichTextTextStyle>;
    protected _stylesStack: StackLevel[] = [];
    /**
     * required for masks
     */
    protected _allStackItems: StackLevel[] = [];
    protected _lines: Line[] = [];
    protected _cursor: Point = new Point(0, 0);
    protected _depth = 0;

    public readonly resolution: number;
    public readonly resourceLoader?: ResourceLoader;
    public nodeTree: Node[];
    private _lang: string;

    constructor(
        defaultStyle: RichTextTextStyle,
        nodeTree: Node[],
        stylesAvailable: Record<string, RichTextTextStyle>,
        resolution: number,
        resourceLoader?: ResourceLoader,
        lang?: string
    ) {
        this.setNewStyleStackLevel(new RichTextTextStyle(defaultStyle), new Container());

        this.nodeTree = nodeTree;
        this._stylesAvailable = stylesAvailable;
        this.resolution = resolution;
        this.resourceLoader = resourceLoader;
        this._lang = lang ?? '';
    }

    /**
     * Ran be called only once on [[CursorContext]] instance.
     */
    public render(): [Container, LineItemType[]] {
        this._lines.forEach(line => line.render());
        this.alignLines();

        const destroyable = this._lines
            .map(line => line.elements.filter(elem => elem.toDestroy).map(elem => elem.item))
            .flat();

        return [this.getRootContainer(), destroyable];
    }

    public applyMasks(renderer: Renderer): void {
        this._allStackItems.forEach(level => {
            if (level.isMask) {
                const {container} = level;
                const {parent} = container;
                const bound = container.getBounds();

                parent.removeChild(container);

                const texture = renderer.generateTexture(container, {
                    scaleMode: SCALE_MODES.LINEAR,
                    resolution: renderer.resolution,
                    region: new Rectangle(bound.x, bound.y, bound.width, bound.height)
                });

                const combinedSprite = new Sprite(texture);

                combinedSprite.x = bound.x;
                combinedSprite.y = bound.y;
                parent.addChild(combinedSprite);
                parent.mask = combinedSprite;
            }
        });
    }

    public newLine(height: number, style: RichTextTextStyle): Line {
        this._cursor.set(0, 0);
        this._lines.forEach(line => this._cursor.y += line.height);

        const line = new Line(height, style);

        this._lines.push(line);

        return line;
    }

    /**
     * add an element to line
     */
    public addToLine(elem: Container, width: number, height: number, descent: number, toDestroy?: boolean): void {
        if (!this.getLastLine()) {
            const style = this.getCurrentLevelStyle();

            this.newLine(height, style);
        }

        this
            .getLastLine()!
            .addToLine(elem, width, height, descent, toDestroy);

        this._cursor.x += width;
        this.getCurrentLevelContainer().addChild(elem);
    }

    public getCurrentLevelStyle(): RichTextTextStyle {
        return this.getStyleStackLevel(this._depth).style;
    }

    public getCurrentLevelContainer(): Container {
        return this.getStyleStackLevel(this._depth).container;
    }

    public setCurrentLevelContainer(container: Container): Container {
        return this.getStyleStackLevel(this._depth).container = container;
    }

    public setCurrentLevelIsMask(isMask?: boolean): boolean {
        return this.getStyleStackLevel(this._depth).isMask = !!isMask;
    }

    public getRootContainer(): Container {
        return this.getStyleStackLevel(0).container;
    }

    public setCurrentStyleByName(name: string): void {
        const style = this._stylesAvailable[`${name}_${this._lang}`] ?? this._stylesAvailable[name];

        if (!style) {
            throw new Error(`RichText - There is no such style (${name}), default style will be used!`);
        }

        const level = this._stylesStack[this._depth];
        const containerNew = new Container();
        const containerParent = level.container.parent;
        const containerOld = level.container;

        if (containerParent) {
            containerParent.addChild(containerNew);
            containerParent.removeChild(containerOld);
        }

        level.style = new RichTextTextStyle(style);
        level.container = containerNew;
    }

    /**
     * setDepth must be called before each teg precessing
     */
    public setDepth(depth: number): void {
        this._stylesStack.length = depth + 1;

        // create style for current level if not exists
        const level = this._stylesStack[depth - 1];
        const currentLevelContainer = new Container();

        level.container.addChild(currentLevelContainer);

        this.setNewStyleStackLevel(new RichTextTextStyle(level.style), currentLevelContainer, depth);
        this._depth = depth;
    }

    get depth(): number {
        return this._depth;
    }

    public getCursor(): Point {
        const cursor = this._cursor.clone();

        this._stylesStack.forEach(level => {
            if (level.cursorOffset) {
                cursor.x += level.cursorOffset.x;
                cursor.y += level.cursorOffset.y;
            }
        });

        return cursor;
    }

    public setCurrentLevelCursorOffset(cursorOffset: Point): Point {
        return this.getStyleStackLevel(this._depth).cursorOffset = cursorOffset;
    }

    protected setNewStyleStackLevel(style: RichTextTextStyle, container: Container, depth = 0): CursorContext {
        const level = {
            style,
            container,
            level: depth
        };

        this._stylesStack[depth] = level;
        this._allStackItems.push(level);

        return this;
    }

    protected getStyleStackLevel(depth: number): StackLevel {
        return this._stylesStack[depth];
    }

    protected getLastLine(): Line | undefined {
        return this._lines[this._lines.length - 1];
    }

    protected alignLines(): void {
        const verticalAlignLayout: [VerticalAlign, number, Line[]][] = [];

        this._lines.forEach(line => {
            const lastBlock = verticalAlignLayout[verticalAlignLayout.length - 1];

            if (!lastBlock || lastBlock[0] !== line.verticalAlign) {
                return verticalAlignLayout.push([line.verticalAlign, line.height, [line]]);
            }

            lastBlock[2].push(line);
            lastBlock[1] += line.height;
        });

        verticalAlignLayout.forEach(([verticalAlign, height, lines]) => {
            let delta = 0;

            if (verticalAlign === 'bottom') {
                delta = -height;
            } else if (verticalAlign === 'center') {
                delta = -height / 2;
            }

            lines.forEach(line => line.elements.forEach(elem => elem.item.y += delta));
        });
    }
}
